/**
 * Shared post-authentication redirect logic, used by both the email login flow
 * (`features/login.tsx`) and the OAuth callback (`features/auth-callback.tsx`).
 *
 * Both flows answer the same question: "where does this user go now that
 * they're signed in?" with the same priority. Centralizing it keeps the two
 * entry points in lockstep.
 *
 * The draft check (priority 2) exists because the email-verification round-trip
 * drops query params: a user who started an assessment, hit the sign-in gate,
 * verified their email, and came back has lost the `?returnTo=/assess` we'd
 * otherwise rely on. The persisted "submit-pending" draft is how we recover the
 * "finish unlocking my report" intent in that case.
 */

import { loadDraft } from "@/features/assess/draft";
import { isStaffRole } from "@/lib/roles";

/** localStorage key holding the path to return to after an OAuth round-trip. */
export const AUTH_RETURN_TO_KEY = "boi:auth-return-to";

/**
 * localStorage key holding the path to return to after the email-verification
 * round-trip. Separate from {@link AUTH_RETURN_TO_KEY} on purpose: the two flows
 * have different lifetimes (OAuth is a seconds-long redirect; a verification
 * email can sit unopened for hours), so they must not share or clobber a stash.
 */
export const SIGNUP_RETURN_TO_KEY = "boi:signup-return-to";

/**
 * How long a stashed signup return-to stays valid (24 h). Tied to Supabase's
 * default email-confirmation link validity: a signup stash can legitimately sit
 * for the life of the verification email, but it must not silently hijack an
 * unrelated login days later.
 */
export const SIGNUP_RETURN_TO_TTL_MS = 24 * 60 * 60_000;

interface SignupReturnToStash {
  path: string;
  at: number;
}

/** localStorage key for the one-shot marker binding a callback to a flow we started. */
export const OAUTH_IN_FLIGHT_KEY = "boi:oauth-in-flight";

/** How long a started OAuth flow stays valid for its callback (15 minutes). */
export const OAUTH_FLOW_TTL_MS = 15 * 60_000;

interface OauthFlowMarker {
  nonce: string;
  at: number;
}

/**
 * Record that THIS browser started an OAuth flow, just before navigating to the
 * provider. Supabase's implicit flow can't reliably round-trip an OAuth `state`
 * back to the fragment, so this client-side marker is what lets the callback
 * distinguish a flow we initiated from an attacker-supplied callback URL phished
 * onto a victim (login-CSRF). Best-effort; write failures are non-fatal.
 */
export function markOauthFlowStarted(): void {
  try {
    const marker: OauthFlowMarker = { nonce: crypto.randomUUID(), at: Date.now() };
    localStorage.setItem(OAUTH_IN_FLIGHT_KEY, JSON.stringify(marker));
  } catch {
    // Non-fatal: a missing marker fails the callback closed (no exchange).
  }
}

/**
 * Read + REMOVE the OAuth flow marker (consume exactly once). Returns `true`
 * only when a well-formed marker existed and is still fresh, i.e. the callback
 * belongs to a flow this browser recently started. Missing, malformed, or stale
 * markers return `false` so the callback fails closed and refuses to exchange.
 */
export function consumeFreshOauthFlow(): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(OAUTH_IN_FLIGHT_KEY);
    localStorage.removeItem(OAUTH_IN_FLIGHT_KEY);
  } catch {
    return false;
  }
  if (!raw) {
    return false;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as OauthFlowMarker).at === "number"
    ) {
      return Date.now() - (parsed as OauthFlowMarker).at < OAUTH_FLOW_TTL_MS;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * A redirect target is safe only if it is a same-origin absolute path. This
 * rejects external URLs and protocol-relative `//host` paths, which the browser
 * would treat as off-site navigations (an open-redirect vector).
 */
export function isSafeInternalPath(path: string | null | undefined): path is string {
  return typeof path === "string" && path.startsWith("/") && !path.startsWith("//");
}

/**
 * Read and clear the OAuth return-to path stashed by `OAuthButtons` before the
 * provider round-trip. Returns `null` when absent, invalid, or unreadable.
 */
export function consumeStoredReturnTo(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_RETURN_TO_KEY);
    localStorage.removeItem(AUTH_RETURN_TO_KEY);
    return isSafeInternalPath(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Stash where to return to after the email-verification round-trip, just before
 * navigating to `/verify-email`. The verification link re-enters the app via
 * Supabase and lands on `/login` with no query string, so the query-param chain
 * we'd otherwise rely on is gone by then; this browser-local stash is what
 * recovers the intent (e.g. an invitee returning to `/report/invite/<token>`).
 *
 * Mirrors `OAuthButtons.start`: always set OR clear the key, so an abandoned
 * signup attempt can't leave a stale value that hijacks a later login. Only safe
 * internal paths are written; anything else removes the key. Best-effort: write
 * failures are non-fatal (the flow degrades to the role-based redirect).
 *
 * Limitation: localStorage is per-browser. If the verification email is opened
 * on a different browser or device, the stash isn't there and the user lands on
 * their role-based home, unchanged from prior behaviour, invite still pending.
 */
export function storeSignupReturnTo(path: string | null | undefined): void {
  try {
    if (isSafeInternalPath(path)) {
      const stash: SignupReturnToStash = { path, at: Date.now() };
      localStorage.setItem(SIGNUP_RETURN_TO_KEY, JSON.stringify(stash));
    } else {
      localStorage.removeItem(SIGNUP_RETURN_TO_KEY);
    }
  } catch {
    // Non-fatal: without the stash we fall back to draft/role-based redirect.
  }
}

/**
 * Read + REMOVE the signup return-to stash (consume exactly once). Returns the
 * path only when a well-formed stash exists, is still a safe internal path, and
 * is within {@link SIGNUP_RETURN_TO_TTL_MS}. Missing, malformed, legacy, or
 * expired stashes return `undefined` so a stale entry can't redirect an
 * unrelated login. Defensive against bad JSON: never throws.
 */
export function consumeSignupReturnTo(): string | undefined {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SIGNUP_RETURN_TO_KEY);
    localStorage.removeItem(SIGNUP_RETURN_TO_KEY);
  } catch {
    return undefined;
  }
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as SignupReturnToStash).at === "number" &&
      isSafeInternalPath((parsed as SignupReturnToStash).path) &&
      Date.now() - (parsed as SignupReturnToStash).at < SIGNUP_RETURN_TO_TTL_MS
    ) {
      return (parsed as SignupReturnToStash).path;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

interface RedirectContext {
  role?: string | null;
  /** An explicit target (login's `?returnTo=` query param, or the OAuth stored path). */
  returnTo?: string | null;
}

/**
 * Resolve where to send a user immediately after they authenticate. Priority:
 *   1. An explicit, validated `returnTo` supplied by the caller.
 *   2. An assessment draft mid-submission → `/assess`, where the wizard
 *      auto-finishes the submission and lands on the report.
 *   3. Role-based home: staff → `/expert`, everyone else → `/history`.
 */
export function resolveAuthRedirect({ role, returnTo }: RedirectContext): string {
  if (isSafeInternalPath(returnTo)) {
    return returnTo;
  }
  if (loadDraft()?.status === "submit-pending") {
    return "/assess";
  }
  return isStaffRole(role) ? "/expert" : "/history";
}
